import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend } from 'recharts';
import { creditResponseType } from "../common/type";
import { themeColors } from "../common/colors";

type CreditRadarChartTypeProps = {
    data: any;
    target: string;
    raderChartSize: number[];
    legendFontSize?: string;
    isExportingPDF?: boolean;
};

type RenderTypeProps = {
    x: number;
    y: number;
    textAnchor: string;
    payloadvalue: string;
    previousCredit: string;
    latestCredit: string;
};

// Material-UIのスタイル設定
const useStyles = makeStyles({
    RadarChartStyle: {
        '& > svg': {
            overflow: 'visible',
            transform: 'scale(.9)',
        },
        '& > svg.recharts-surface': {
            paddingBottom: '10px',
        }
    },
    RadarChartPDFStyle: {
        // PDFに出力したときに、ラベルの上下位置を調整する
        '& > div ul li span': {
            position: 'relative',
            bottom: '6px',
        },
        '& > svg': {
            overflow: 'visible',
            transform: 'scale(.9)',
        },
        '& > svg.recharts-surface': {
            paddingBottom: '10px',
        }
    },
});

const CreditRadarChartPart = ({
    data,
    target,
    raderChartSize,
    legendFontSize,
    isExportingPDF
}: CreditRadarChartTypeProps) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const [isData, setIsData] = useState<any[]>([]);
    const [maxAxisValue, setMaxAxisValue] = useState<number>(0)

    const RadarChartStyle = {
        width: '100%',
        height: 'auto',
        maxWidth: `${raderChartSize[0]}px`,
        maxHeight: `${raderChartSize[1]}px`,
        margin: 'auto',
    };
    const LedgendFontSize = legendFontSize ? legendFontSize : 'max(.7vw, .7rem)';

    const SUBJECT_ITEM: string[] = Array.from({ length: 8 }, (_, i) => t(`common.ps8_${i + 1}`));

    const createData = (data: creditResponseType) => {
        const p_c = data.previous_credits;
        const l_c = data.latest_credits;
        const b_c = data.base_credits;
        const defaultValue = 0.0;
        const maxLatestCredit = Math.max(...Object.values(p_c));
        setMaxAxisValue(Math.ceil(maxLatestCredit / 10) * 10);
        /** previousCredit:前回 latestCredit:最新 baseCredit:標準*/
        if (target === "seData") {
            const se_data = [
                {
                    subject: SUBJECT_ITEM[0],
                    previousCredit: p_c.ps8_credit_1 || defaultValue,
                    latestCredit: l_c.ps8_credit_1 || defaultValue,
                    baseCredit: b_c.ps8_credit_1 || defaultValue
                },
                {
                    subject: SUBJECT_ITEM[1],
                    previousCredit: p_c.ps8_credit_2 || defaultValue,
                    latestCredit: l_c.ps8_credit_2 || defaultValue,
                    baseCredit: b_c.ps8_credit_2 || defaultValue
                },
                {
                    subject: SUBJECT_ITEM[2],
                    previousCredit: p_c.ps8_credit_3 || defaultValue,
                    latestCredit: l_c.ps8_credit_3 || defaultValue,
                    baseCredit: b_c.ps8_credit_3 || defaultValue
                },
                {
                    subject: SUBJECT_ITEM[3],
                    previousCredit: p_c.ps8_credit_4 || defaultValue,
                    latestCredit: l_c.ps8_credit_4 || defaultValue,
                    baseCredit: b_c.ps8_credit_4 || defaultValue
                },
            ];
            setIsData(se_data);
        } else if (target === "tsData") {
            const ts_data = [
                {
                    subject: SUBJECT_ITEM[4],
                    previousCredit: p_c.ps8_credit_5 || defaultValue,
                    latestCredit: l_c.ps8_credit_5 || defaultValue,
                    baseCredit: b_c.ps8_credit_5 || defaultValue
                },
                {
                    subject: SUBJECT_ITEM[5],
                    previousCredit: p_c.ps8_credit_6 || defaultValue,
                    latestCredit: l_c.ps8_credit_6 || defaultValue,
                    baseCredit: b_c.ps8_credit_6 || defaultValue
                },
                {
                    subject: SUBJECT_ITEM[6],
                    previousCredit: p_c.ps8_credit_7 || defaultValue,
                    latestCredit: l_c.ps8_credit_7 || defaultValue,
                    baseCredit: b_c.ps8_credit_7 || defaultValue
                },
                {
                    subject: SUBJECT_ITEM[7],
                    previousCredit: p_c.ps8_credit_8 || defaultValue,
                    latestCredit: l_c.ps8_credit_8 || defaultValue,
                    baseCredit: b_c.ps8_credit_8 || defaultValue
                },
            ];
            setIsData(ts_data);
        }
    };

    useEffect(() => {
        createData(data);
    }, []);

    // 項目の横に数値を設置
    const RenderTypeA = ({ x, y, textAnchor, payloadvalue, previousCredit, latestCredit }: RenderTypeProps) => {
        return (
            <>
                <text x={x} y={y} textAnchor={textAnchor}>
                    <tspan>{payloadvalue}</tspan>
                    <tspan dx="10" fontWeight="bold" fill={themeColors[0]}>
                        {latestCredit}
                        <tspan fontWeight="bold" fill={themeColors[2]}>（{previousCredit}）</tspan>
                    </tspan>
                </text>
            </>
        );
    };

    // 項目の下に数値を設置
    const RenderTypeB = ({ x, y, textAnchor, payloadvalue, previousCredit, latestCredit }: RenderTypeProps) => {
        const breaks = payloadvalue.length >= 8 ? true : false;
        const value = breaks ? payloadvalue.replace(/(な|を|and)/g, '$1|').split('|') : payloadvalue;
        return (
            <>
                <text x={x} y={y} textAnchor={textAnchor}>
                    {breaks ? (
                        <>
                            <tspan x={x} y={y - 15} dy="0">{value[0]}</tspan>
                            <tspan x={x} y={y - 15} dy="18">{value[1]}</tspan>
                        </>
                    ) : (
                        <>
                            <tspan>{value}</tspan>
                        </>
                    )}
                </text>
                <text x={x} y={y} textAnchor={textAnchor}>
                    <tspan dy="25" fontWeight="bold" fill={themeColors[0]}>
                        {latestCredit}
                        <tspan fontWeight="bold" fill={themeColors[2]}>（{previousCredit}）</tspan>
                    </tspan>
                </text>
            </>
        );
    };

    const renderCustomItem = (label: any) => {
        const { x, y, textAnchor, index, payload } = label;
        const values = isData;
        const fixY = index === 0 ? y - 10 : index === 2 ? y + 10 : y;
        const renderTypeProps: RenderTypeProps = {
            x: x,
            y: fixY,
            textAnchor: textAnchor,
            payloadvalue: payload.value,
            previousCredit: values![index].previousCredit,
            latestCredit: values![index].latestCredit,
        };
        return (
            <>
                {index === 0 && (RenderTypeA({ ...renderTypeProps }))}
                {index === 1 && (RenderTypeB({ ...renderTypeProps }))}
                {index === 2 && (RenderTypeA({ ...renderTypeProps }))}
                {index === 3 && (RenderTypeB({ ...renderTypeProps }))}
            </>
        );
    };

    return (
        <>
            <RadarChart
                outerRadius={120}
                width={raderChartSize[0]}
                height={raderChartSize[1]}
                data={isData}
                margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                }}
                className={isExportingPDF ? classes.RadarChartPDFStyle : classes.RadarChartStyle}
                style={RadarChartStyle}
            >
                <PolarGrid />
                <PolarAngleAxis
                    dataKey="subject"
                    tick={renderCustomItem} />
                <PolarRadiusAxis
                    angle={90}
                    domain={[0, maxAxisValue]}
                />
                <defs>
                    <linearGradient
                        id="gradationColor"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="0%"
                    >
                        <stop offset="0%" stopColor={themeColors[0]} />
                        <stop offset="100%" stopColor={themeColors[1]} />
                    </linearGradient>
                </defs>
                <Radar
                    name={t("credit.legendLatest")}
                    dataKey="latestCredit"
                    fill="url(#gradationColor)"
                    fillOpacity={0.8} />
                <Radar
                    name={t("credit.legendPrevious")}
                    dataKey="previousCredit"
                    fill={themeColors[2]}
                    fillOpacity={0.8}
                />
                <Radar
                    name={t("credit.legendBase")}
                    dataKey="baseCredit"
                    stroke={themeColors[3]}
                    fill="transparent"
                    fillOpacity={1}
                />
                <Legend wrapperStyle={{ width: "100%", bottom: "10px", left: "0", fontSize: LedgendFontSize }} />
            </RadarChart>
        </>
    );
};

export default CreditRadarChartPart;
